import { fetchFromLambda } from '@agyletime/vue-3-common/src/utils/apiService';
import queryToString from '../../utils/queryToString';
import { InteractionSummaryTypes } from '../../types';
import { FocusSummary } from '@/types/interactionSummary';
import moment from 'moment-timezone';

type WorkstreamInteractionInterval = {
    date: string; // "2022-12-31"
    averageHandleTime: number;
    interactions: number;
    totalHandleTime: number;
};
export type WorkstreamInteractionGrouping = {
    groupName: number | string; // workload name or id
    totalHandleTime: number;
    totalInteractions: number;
    averageHandleTime: number;
};
type WorkstreamInteractionTeamDataUser = {
    userId: number;
    name: string; // part of the response
    totalHandleTime: number;
    totalInteractions: number;
    averageHandleTime: number;
    groupingType: WorkstreamInteractionGrouping[]; // tabular data for user level
    // Not exist on response
    totalResolved: number;
};
type WorkstreamInteractionTeamData = {
    teamId: number;
    name: string; // part of response?
    // totalHandleTime?: number;
    // totalInteractions?: number;
    totalInteractions: number;
    totalHandleTime: number;
    isStandaloneTeam?: boolean;
    // averageHandleTime?: number;
    groupingType: WorkstreamInteractionGrouping[]; // tabular data for team level
    users: WorkstreamInteractionTeamDataUser[];
};
type WorkstreamInteractionWorkstreamDataGrouping = {
    groupName: number | string; // workload name or id
    totalInteractions: number;
    totalHandleTime: number;
    averageHandleTime: number;
    // Not exist on response
    percentageResolved: number;
    totalResolved: number;
};
type WorkstreamInteractionWorkstream = {
    averageHandleTime: number;
    grouping: WorkstreamInteractionWorkstreamDataGrouping[];
    totalHandleTime: number;
    totalInteractions: number;
    workstreamName: string;
};

export type WorkstreamInteractionIntervalData = {
    [key: number | string]: WorkstreamInteractionInterval[];
};
export type WorkstreamInteractionGroupOrTeamData = {
    averageHandleTime: number;
    groupingType: WorkstreamInteractionGrouping[]; // tabular data for group or team level
    // id: number;
    teamId: number;
    totalHandleTime: number;
    totalInteractions: number;
    users?: WorkstreamInteractionTeamDataUser[]; // undefined if isStandaloneTeam=false
    teams?: WorkstreamInteractionTeamData[]; // undefined if isStandaloneTeam=true
    // Not exist on response
    name: string; // group name
    // if true then grouping is Team > User otherwise Group > Team > User
    isStandaloneTeam: boolean;
};
export type WorkstreamInteractionWorkstreamData = {
    [key: number | string]: WorkstreamInteractionWorkstream;
};
export type WorkstreamInteractionData = {
    teamData: {
        byType: WorkstreamInteractionGroupOrTeamData[];
        byWorkload: WorkstreamInteractionGroupOrTeamData[];
    };
    workstreamData: {
        byType: WorkstreamInteractionWorkstreamData;
        byWorkload: WorkstreamInteractionWorkstreamData;
    };
    intervalData: WorkstreamInteractionIntervalData;
    focusData?: FocusSummary[];
};
export type SeriesData = {
    name: string;
    data: number[];
    visible: boolean;
};

export const getData = async (
    filters: InteractionSummaryTypes.InteractionSummaryFilter,
    retrieveFocus?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    teams?: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    users?: any
): Promise<WorkstreamInteractionData> => {
    const promises = [];
    const urls = [];
    urls.push(generateInteractionSummaryUrl('group', filters));
    urls.push(generateInteractionSummaryUrl('workstream', filters));
    urls.push(generateInteractionSummaryUrl('interval', filters));
    for (const url of urls) {
        promises.push(fetchFromLambda(url));
    }
    if (retrieveFocus) {
        promises.push(retrieveFocusData(filters, teams, users));
    }
    const [
        groupData, workstreamData, intervalData, focusData
    ] = await Promise.all(
        promises
    );

    // console.log('Group Data:', groupData);
    // console.log('Workstream Data:', workstreamData);
    // console.log('Interval Data:', intervalData);

    return {
        teamData: groupData as WorkstreamInteractionData['teamData'],
        workstreamData: workstreamData as WorkstreamInteractionData['workstreamData'],
        intervalData: intervalData as WorkstreamInteractionIntervalData,
        focusData: focusData as FocusSummary[]
    };
};

const generateInteractionSummaryUrl = (
    endPath: string,
    filters: InteractionSummaryTypes.InteractionSummaryFilter
): string => {
    const PERF_METRICS_BASE_PATH = '/performance-metrics/interactions/';
    let requestUri = `${PERF_METRICS_BASE_PATH}${endPath}`;
    const queryParams = queryToString(filters);
    if (queryParams) {
        requestUri = `${requestUri}?${queryParams}`;
    }

    return requestUri;
};

export const retrieveFocusData = async (
    filters: InteractionSummaryTypes.InteractionSummaryFilter,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    teams: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    users: any
): Promise<FocusSummary[]> => {
    const baseUrl = '/workactivity/focus/summary';
    const focusQueryParams = buildFocusQueryParams(filters, teams, users);
    const requestUrl = `${baseUrl}?${focusQueryParams}`;
    const result: FocusSummary[] = await fetchFromLambda(requestUrl);
    return result;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const retrieveFocusConfig = async (): Promise<any> => {
    const url = '/connectors/features/focus_event_report';
    const result = await fetchFromLambda(url);
    return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, complexity
const buildFocusQueryParams = (filters: any, teams: any, users: any): string => {
    const { fromDate, toDate, teamIds, userIds, roleIds, tags, timezone } = filters;
    const queryParams = [];
    if (fromDate) {
        const formattedStartDate = moment(fromDate).tz(timezone).format('YYYY-MM-DD');
        queryParams.push(`startDate=${formattedStartDate}`);
    }
    if (toDate) {
        const formattedEndDate = moment(toDate).tz(timezone).format('YYYY-MM-DD');
        queryParams.push(`endDate=${formattedEndDate}`);
    }
    if (teamIds || userIds || tags || roleIds) {
        let usersQuery = 'userIds=';
        if (teamIds) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const teamUsers = users.filter((user: any) => teamIds.includes(user.teamId)).map((user: any) => user.id);
            usersQuery += `${teamUsers.join(',')}`;
        }
        if (userIds) {
            for (const id of userIds) {
                usersQuery += `${id},`;
            }
        }
        if (tags) {
            const splitTags = tags.split(',');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tagUsers = users.filter((user: any) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return user.tags.some((tag: any) => splitTags.includes(tag.text));
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }).map((user: any) => user.id);
            usersQuery += `${tagUsers.join(',')}`;
        }
        if (roleIds) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const roleUsers = users.filter((user: any) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return user.roles.some((role: any) => roleIds.includes(role.id));
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            }).map((user: any) => user.id);
            usersQuery += `${roleUsers.join(',')}`;
        }
        queryParams.push(usersQuery);
    }
    return queryParams.join('&');
};
